import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const ServicesPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      id: 'concrete',
      title: 'Concrete Services',
      description: 'Professional concrete pouring, finishing, and installation for driveways, foundations, sidewalks, and more. Our expert team delivers durable, high-quality concrete work that stands the test of time.',
      features: ['Driveways', 'Foundations', 'Sidewalks', 'Floors', 'Slabs', 'Repairs'],
      image: 'https://public.readdy.ai/ai/img_res/beb687555f2e82b18b8eeb2d715ba7c1.jpg',
    },
    {
      id: 'patio',
      title: 'Patio Installation',
      description: 'Transform your outdoor space with custom patio designs. We create beautiful, functional patios that enhance your home and provide the perfect setting for outdoor entertainment and relaxation.',
      features: ['Custom Designs', 'Stamped Concrete', 'Decorative Patterns', 'Outdoor Kitchens', 'Fire Pits', 'Seating Areas'],
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20custom%20patio%20installation%20with%20decorative%20concrete%20patterns%2C%20outdoor%20living%20space%20with%20elegant%20design%2C%20workers%20installing%20patio%20surface%2C%20professional%20craftsmanship%2C%20simple%20neutral%20background%20highlighting%20the%20patio%20work%2C%20warm%20natural%20lighting%2C%20inviting%20outdoor%20entertainment%20area&width=800&height=600&seq=service-page-patio&orientation=landscape',
    },
    {
      id: 'pavers',
      title: 'Paver Installation',
      description: 'Expert paver installation for driveways, walkways, and decorative landscaping features. We work with a variety of paver styles and patterns to create stunning, durable surfaces.',
      features: ['Driveway Pavers', 'Walkways', 'Patios', 'Pool Decks', 'Retaining Walls', 'Custom Patterns'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20paver%20installation%20with%20intricate%20geometric%20patterns%2C%20workers%20carefully%20placing%20interlocking%20pavers%2C%20driveway%20and%20walkway%20construction%2C%20detailed%20craftsmanship%2C%20simple%20neutral%20background%20highlighting%20the%20paver%20work%2C%20bright%20natural%20lighting%2C%20precise%20and%20clean%20installation%20process&width=800&height=600&seq=service-page-pavers&orientation=landscape',
    },
    {
      id: 'drywall',
      title: 'Drywall Services',
      description: 'Professional drywall installation, repair, and finishing for residential and commercial projects. Our skilled team ensures smooth, flawless walls ready for painting or finishing.',
      features: ['Installation', 'Repairs', 'Texturing', 'Finishing', 'Taping', 'Mudding'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20installation%20and%20finishing%2C%20construction%20worker%20applying%20drywall%20compound%20and%20smoothing%20walls%2C%20interior%20construction%20site%20with%20clean%20workspace%2C%20skilled%20craftsmanship%2C%20simple%20neutral%20background%20highlighting%20the%20drywall%20work%2C%20even%20lighting%2C%20professional%20quality%20finish&width=800&height=600&seq=service-page-drywall&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-brand-gray to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-6xl lg:text-7xl font-black mb-6">Our Services</h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive concrete and construction solutions for residential and commercial projects in Warsaw, Indiana
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 lg:py-32" ref={ref}>
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="inline-block bg-brand-green/10 text-brand-green px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
                    {service.id}
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">{service.title}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">{service.description}</p>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-brand-green text-xl"></i>
                        </div>
                        <span className="text-gray-700 font-medium text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/service/${service.id}`}
                    className="inline-block bg-brand-green text-white px-8 py-4 rounded-full font-semibold hover:bg-brand-green/90 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-green py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for a free quote and consultation
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:5743776776"
              className="bg-white text-brand-gray px-10 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              (574) 377-6776
            </a>
            <a
              href="/#contact"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-semibold hover:bg-white hover:text-brand-green transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;